import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from 'react-router-dom';
import { DashboardPage } from '../pages/DashboardPage';
import { UserManagementPage } from '../pages/UserManagementPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { Layout } from './layout/Layout';
import { CockpitPage } from '../pages/CockpitPage';
import { UserProvider } from '../context/UserProvider';
import { routeUrls } from '../routes';
import { PeriodProvider } from '../context/PeriodProvider';
import { useEffect, useState } from 'react';
import { Role } from '../types/Role';
import { AccessLevel } from './ui/AccessLevel';
import { Restricted } from './ui/Restricted';
import { LoadingScreen } from './ui/LoadingScreen';
import { ToastContainer } from 'react-toastify';
import { ChangeRequestPage } from '../pages/ChangeRequestPage';
import { ChangeRequestSubmissionsPage } from '../pages/ChangeRequestSubmissionsPage';
import { useAuth0 } from '@auth0/auth0-react';
import { config } from '../lib/env';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import {reactPlugin, trackException} from '../lib/appInsights';

export const App = () => {
    const auth0 = useAuth0();
    const msal = useMsal();
    const { isAuthenticated, loginWithRedirect, isLoading, loginWithPopup, getAccessTokenSilently } = auth0;

    const { instance, inProgress, accounts } = msal;

    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState(Role.USER);
    const [accessToken, setAccessToken] = useState<string>();
    const [unexpectedError, setUnexpectedError] = useState<boolean>(false);
    const isMsalAuthenticated = useIsAuthenticated();
    const isRootPath = window.location.pathname === '/';
    const tokenAuth0Request = {
        scopes: 'openid profile email',
    };
    const tokenMsalRequest = {
        account: accounts[0],
        scopes: [config.authentication.msalAuthScope || ''],
    };
    let loginOptions = { authorizationParams: { scope: tokenAuth0Request.scopes } };
    const redirectToRoot = () => {
        window.location.href = '/';
    };

    useEffect(() => {
        const auth0Fetch = async () => {
            if (accessToken) {
                return;
            }

            try {
                const response = await auth0.getAccessTokenSilently({
                    authorizationParams: {
                        audience: config.authentication.audience,
                        scope: 'openid profile email',
                    },
                    detailedResponse: true,
                });

                setAccessToken(response.access_token);
                setLoading(false);
            } catch (e: any) {
                if (e.error == 'login_required') {
                    await auth0.loginWithRedirect(loginOptions);
                }
                setUnexpectedError(true);

                console.error({ name: 'auth0Fetch', error: e});
                throw e;
            }
        };
        const msalFetch = async () => {
            try {
                const response = await instance.acquireTokenSilent(tokenMsalRequest);

                const account = accounts[0];
                const idTokenClaims = account.idTokenClaims;

                const roles = idTokenClaims != undefined ? idTokenClaims.roles : [];

                const msalRole = roles != undefined ? (roles[0] as Role) : Role.USER;

                setRole(msalRole);
                setAccessToken(response.accessToken);
                setLoading(false);
            } catch (e) {
                if (e instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect(tokenMsalRequest);
                } else {
                    setUnexpectedError(true);
                    console.error({ name: 'msalFetch', error: e});
                    throw e;
                }
            }
        };
        const fetchAccessToken = async () => {
            // TODO: Find out if this is enough for knowing it's not a msal auth flow
            if (!isLoading && isAuthenticated) {
                auth0Fetch();
            }

            if (inProgress == InteractionStatus.None && isMsalAuthenticated) {
                msalFetch();
            }
        };

        fetchAccessToken();
    }, [isLoading, isAuthenticated, getAccessTokenSilently, isMsalAuthenticated, inProgress]);

    useEffect(() => {
        if (unexpectedError) {
            redirectToRoot();
        }
    }, [unexpectedError]);

    // Check if the token is still valid every 5 minutes
    // useEffect(() => {
    //     if (accessToken && role) {
    //         const tokenValidityInterval = setInterval(() => {
    //             UsersClient({ userRole: role, accessToken: accessToken || '' })
    //                 .validateToken()
    //                 .catch(async () => {
    //                     await instance.acquireTokenRedirect({ ...tokenRequest, redirectUri: '/' });
    //                 });
    //         }, 300000);
    //
    //         return () => {
    //             clearInterval(tokenValidityInterval);
    //         };
    //     }
    //
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [accounts, accessToken]);

    useEffect(() => {
        // If you login from any other url than "/" msal-library will throw an error because the redirectUri is not the same
        if (inProgress === InteractionStatus.None && !isAuthenticated && !isRootPath) {
            redirectToRoot();
        }
    }, [inProgress, isAuthenticated, isRootPath]);

    // Access token is being fetched
    if (loading && (isAuthenticated || isMsalAuthenticated)) {
        return (
            <>
                <LoadingScreen />
            </>
        );
    }

    return (
        <AppInsightsErrorBoundary onError={() => <h1>I believe something went wrong</h1>} appInsights={reactPlugin}>
            <AppInsightsContext.Provider value={reactPlugin}>
                <div className="container mx-auto h-screen">
                    {accessToken && (isAuthenticated || isMsalAuthenticated) ? (
                        <RouterProvider
                            router={createBrowserRouter(
                                createRoutesFromElements(
                                    <Route
                                        element={
                                            <UserProvider accessToken={accessToken} role={role}>
                                                <PeriodProvider>
                                                    <Layout>
                                                        <Outlet />
                                                    </Layout>
                                                </PeriodProvider>
                                            </UserProvider>
                                        }
                                    >
                                        <Route path="/" element={<DashboardPage />} />
                                        <Route path={routeUrls.DASHBOARD} element={<DashboardPage />} />
                                        <Route
                                            path={routeUrls.USERMANAGEMENT}
                                            element={
                                                <AccessLevel role={role} messageElement={<Restricted />}>
                                                    <UserManagementPage />
                                                </AccessLevel>
                                            }
                                        />
                                        <Route
                                            path={`${routeUrls.DASHBOARD}/:id`}
                                            element={
                                                <AccessLevel role={Role.ADMIN} messageElement={<Restricted />}>
                                                    <DashboardPage />
                                                </AccessLevel>
                                            }
                                        />
                                        <Route
                                            path={routeUrls.COCKPIT}
                                            element={
                                                <AccessLevel role={Role.ADMIN} messageElement={<Restricted />}>
                                                    <CockpitPage />
                                                </AccessLevel>
                                            }
                                        />
                                        <Route
                                            path={routeUrls.SUBMISSIONS}
                                            element={
                                                <AccessLevel role={Role.ADMIN} messageElement={<Restricted />}>
                                                    <ChangeRequestSubmissionsPage />
                                                </AccessLevel>
                                            }
                                        />
                                        <Route path={routeUrls.CHANGE_REQUESTS} element={<ChangeRequestPage />} />
                                        <Route
                                            path={`${routeUrls.CHANGE_REQUESTS}/:id`}
                                            element={
                                                <AccessLevel role={Role.ADMIN} messageElement={<Restricted />}>
                                                    <ChangeRequestPage />
                                                </AccessLevel>
                                            }
                                        />
                                        <Route path="*" element={<NotFoundPage />} />
                                    </Route>,
                                ),
                            )}
                        />
                    ) : (
                        <div className="flex flex-col w-full h-full items-center justify-center gap-3 p-3">
                            <img src="https://cohedron.nl/wp-content/uploads/2021/11/Cohedron-LOGO.svg" alt="Cohedron logo" className="max-h-20" />
                            <div className="text-3xl text-primary">EcoSaver</div>

                            <button className="rounded bg-primary text-white px-5 py-2 mt-10" onClick={() => instance.loginRedirect()}>
                                Inloggen met je werk account
                            </button>

                            <button className="rounded bg-primary text-white px-5 py-2 mt-10" onClick={() => loginWithRedirect(loginOptions)}>
                                Handmatig inloggen
                            </button>
                        </div>
                    )}

                    <ToastContainer
                        position="bottom-right"
                        autoClose={5000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="dark"
                    />
                </div>
            </AppInsightsContext.Provider>
        </AppInsightsErrorBoundary>
    );
};
